import { useRef, type KeyboardEvent, type ReactNode } from 'react'
import clsx from 'clsx'
import { DisabledReason } from './DisabledReason'
import { Tooltip } from './Tooltip'

/**
 * A segment's fill: `neutral` gray, `info` the app's selected blue (as the
 * rail's selected item), `yellow` (Theme's sun), or a status tint. Each is
 * three steps deep: pointed at, selected, and selected while pointed at.
 */
export type SegmentTone = 'neutral' | 'info' | 'yellow' | 'success' | 'warning' | 'danger'

/** Which way an icons-only control runs. */
export type SegmentOrientation = 'horizontal' | 'vertical'

interface SegmentedControlOptionBase<T extends string> {
  value: T
  /** Shown on the segment; with icons alone, its name and tooltip instead. */
  label: string
  /** Its fill while selected: info blue unless set, such as `danger` for Delete. */
  tone?: SegmentTone
  /** Its fill while pointed at (and not selected): gray unless set. */
  hoverTone?: SegmentTone
  /**
   * Why this one choice can't be made here: the segment is disabled, the
   * arrow keys pass over it, and the reason shows on hover and focus. It
   * still shows selected when it's the value.
   */
  disabledReason?: string | null
}

/** A segment named by its label alone. */
export interface SegmentedControlOption<T extends string> extends SegmentedControlOptionBase<T> {
  icon?: never
}

/** A segment with an icon, as every segment has under `withIcons` or `iconOnly`. */
export interface SegmentedControlIconOption<
  T extends string,
> extends SegmentedControlOptionBase<T> {
  /** Drawn in the segment's text color and sized by the control; `aria-hidden`. */
  icon: ReactNode
}

// What each segment shows. Icons are all or nothing: with them, every option
// needs one. Only icons alone may run vertically, and only icons with labels stack.
type SegmentedControlDisplayProps<T extends string> =
  | {
      withIcons?: false
      iconOnly?: false
      stacked?: never
      orientation?: never
      options: readonly SegmentedControlOption<T>[]
    }
  | {
      withIcons: true
      iconOnly?: false
      /** Each icon above its label in smaller type, for a narrow row such as the sidebar's. */
      stacked?: boolean
      orientation?: never
      options: readonly SegmentedControlIconOption<T>[]
    }
  | {
      /** Icons alone, each named (and tooltipped) by its label. */
      iconOnly: true
      withIcons?: true
      stacked?: never
      orientation?: SegmentOrientation
      options: readonly SegmentedControlIconOption<T>[]
    }

type SegmentedControlProps<T extends string> = SegmentedControlDisplayProps<T> & {
  /** Null when no segment is selected, such as a "set all" over a mixed list. */
  value: T | null
  onChange: (value: T) => void
  /** Names the group, such as the row it decides for. */
  ariaLabel: string
  /** Ids of text that explains the group, such as why it is locked. */
  ariaDescribedBy?: string
  disabled?: boolean
  /** The segments share the track's whole width, rather than each fitting its content. */
  fullWidth?: boolean
  /** Every segment as wide as the widest, side by side (not with `fullWidth`). */
  equalWidths?: boolean
  /**
   * `tile`: a rail tile tall, beside rail-sized buttons (the footer's
   * Expansion and header's Theme toggles), with larger type. `field`: as tall
   * as a form field (`FieldInput`), for a choice among a form's fields, so the
   * form's controls line up. Side by side only.
   */
  size?: 'default' | 'tile' | 'field'
  className?: string
}

// A segment's fill while selected, a step stronger again while pointed at (as
// the rail's selected item deepens).
const SELECTED_FILL_CLASS: Record<SegmentTone, string> = {
  neutral:
    'bg-[color:var(--segment-neutral-selected-bg)] text-default hover:bg-[color:var(--segment-neutral-selected-hover-bg)]',
  info: 'bg-[color:var(--segment-info-selected-bg)] text-[color:var(--segment-info-text)] hover:bg-[color:var(--segment-info-selected-hover-bg)]',
  yellow:
    'bg-[color:var(--segment-yellow-selected-bg)] text-[color:var(--segment-yellow-text)] hover:bg-[color:var(--segment-yellow-selected-hover-bg)]',
  success:
    'bg-[color:var(--segment-success-selected-bg)] text-[color:var(--segment-success-text)] hover:bg-[color:var(--segment-success-selected-hover-bg)]',
  warning:
    'bg-[color:var(--segment-warning-selected-bg)] text-[color:var(--segment-warning-text)] hover:bg-[color:var(--segment-warning-selected-hover-bg)]',
  danger:
    'bg-[color:var(--segment-danger-selected-bg)] text-[color:var(--segment-danger-text)] hover:bg-[color:var(--segment-danger-selected-hover-bg)]',
}

// A segment's fill while pointed at and not selected: a fill, not just darker
// text, so the whole segment shows as the thing to click; lighter than the
// same tone selected.
const HOVER_FILL_CLASS: Record<SegmentTone, string> = {
  neutral: 'hover:bg-[color:var(--segment-neutral-hover-bg)] hover:text-default',
  info: 'hover:bg-[color:var(--segment-info-hover-bg)] hover:text-[color:var(--segment-info-text)]',
  yellow:
    'hover:bg-[color:var(--segment-yellow-hover-bg)] hover:text-[color:var(--segment-yellow-text)]',
  success:
    'hover:bg-[color:var(--segment-success-hover-bg)] hover:text-[color:var(--segment-success-text)]',
  warning:
    'hover:bg-[color:var(--segment-warning-hover-bg)] hover:text-[color:var(--segment-warning-text)]',
  danger:
    'hover:bg-[color:var(--segment-danger-hover-bg)] hover:text-[color:var(--segment-danger-text)]',
}

/**
 * One choice among a few, each a labelled segment in a small track:
 * [Pause][Detach][Delete]. The selected segment is filled, in info blue
 * unless its tone says otherwise (Delete in red), and the others fill on hover
 * (gray unless their hover tone says otherwise), so a list of rows reads down
 * at a glance. It is a radio group: one Tab
 * stop, arrow keys move the choice, and each whole segment is the click
 * target. Use it for a short, exclusive choice made per row or in a toolbar;
 * a longer list belongs in a select.
 *
 * With `withIcons`, each segment shows its icon before its label
 * ([☀ Light][☾ Dark][▭ System]), or above it when `stacked`. With `iconOnly`,
 * icons alone, side by side or (`orientation="vertical"`) in a column, as in
 * the collapsed sidebar; each label names its segment and shows on hover.
 */
export default function SegmentedControl<T extends string>({
  options: segments,
  withIcons = false,
  iconOnly = false,
  stacked = false,
  orientation = 'horizontal',
  value,
  onChange,
  ariaLabel,
  ariaDescribedBy,
  disabled = false,
  fullWidth = false,
  equalWidths = false,
  size = 'default',
  className,
}: SegmentedControlProps<T>) {
  // Either kind of option list, read as one.
  const options: readonly (SegmentedControlOptionBase<T> & { icon?: ReactNode })[] = segments
  const groupRef = useRef<HTMLDivElement>(null)
  const selectedIndex = options.findIndex((option) => option.value === value)
  // One Tab stop: the selected segment, or the first that can be chosen while none is.
  const tabStop =
    selectedIndex === -1
      ? Math.max(
          0,
          options.findIndex((option) => !option.disabledReason)
        )
      : selectedIndex
  const vertical = iconOnly && orientation === 'vertical'
  const showsIcons = withIcons || iconOnly
  const tileSize = size === 'tile' && !vertical && !stacked
  const fieldSize = size === 'field' && !vertical && !stacked

  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0
    if (step === 0) return
    event.preventDefault()
    let next = index
    for (let tries = 0; tries < options.length; tries += 1) {
      next = (next + step + options.length) % options.length
      if (!options[next].disabledReason) break
    }
    if (next === index || options[next].disabledReason) return
    onChange(options[next].value)
    groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus()
  }

  // One shape class per layout, so no two set the same property.
  const segmentShapeClass = vertical
    ? 'w-full py-[5px]'
    : iconOnly
      ? 'px-1.5'
      : stacked
        ? 'flex-col gap-1 px-1 py-1.5 text-[10px]'
        : tileSize
          ? 'gap-1.5 px-2.5 text-xs'
          : fieldSize
            ? 'gap-1 px-2.5 text-xs'
            : 'gap-1 px-2 text-[11px]'

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-disabled={disabled || undefined}
      aria-orientation={vertical ? 'vertical' : undefined}
      className={clsx(
        // Segments 2px apart, as they sit from the track's border (icons alone
        // too), so a hovered one beside the selected one shows as two fills.
        'shrink-0 items-stretch gap-0.5 rounded-md border border-default bg-[color:var(--segment-track-bg)] p-0.5',
        fullWidth || vertical
          ? 'flex w-full'
          : equalWidths
            ? 'inline-grid auto-cols-fr grid-flow-col'
            : 'inline-flex',
        vertical
          ? 'flex-col'
          : stacked
            ? ''
            : tileSize
              ? 'h-[var(--sidebar-tile-size)]'
              : fieldSize
                ? 'h-8'
                : 'h-[26px]',
        disabled && 'opacity-60',
        className
      )}
    >
      {options.map((option, index) => {
        const selected = index === selectedIndex
        const optionDisabled = disabled || Boolean(option.disabledReason)
        const segment = (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={iconOnly ? option.label : undefined}
            tabIndex={index === tabStop ? 0 : -1}
            disabled={optionDisabled}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => move(event, index)}
            className={clsx(
              // No color transition: a segment's fill switches the instant the
              // pointer arrives or leaves, so crossing the 2px gap between two
              // segments can't half-fade one in and out (a flicker).
              'flex items-center justify-center rounded font-medium leading-none disabled:cursor-not-allowed',
              segmentShapeClass,
              (fullWidth || equalWidths) && !vertical && 'min-w-0 flex-1',
              iconOnly && (fullWidth || vertical) && 'w-full',
              selected
                ? SELECTED_FILL_CLASS[option.tone ?? 'info']
                : disabled
                  ? 'text-subtle'
                  : option.disabledReason
                    ? 'text-muted opacity-60'
                    : clsx('text-subtle', HOVER_FILL_CLASS[option.hoverTone ?? 'neutral'])
            )}
          >
            {showsIcons ? (
              <span
                aria-hidden="true"
                className={clsx(
                  'flex shrink-0 items-center justify-center',
                  iconOnly
                    ? '[&_svg]:h-4 [&_svg]:w-4'
                    : stacked || tileSize
                      ? '[&_svg]:h-3.5 [&_svg]:w-3.5'
                      : '[&_svg]:h-3 [&_svg]:w-3'
                )}
              >
                {option.icon}
              </span>
            ) : null}
            {iconOnly ? null : <span className="whitespace-nowrap">{option.label}</span>}
          </button>
        )
        if (!iconOnly) {
          // The whole control's reason is its host's to give; a segment's is its own.
          return option.disabledReason && !disabled ? (
            <DisabledReason
              key={option.value}
              reason={option.disabledReason}
              className={clsx('flex', (fullWidth || equalWidths) && 'min-w-0 flex-1')}
            >
              {segment}
            </DisabledReason>
          ) : (
            segment
          )
        }
        // Icons alone: the label shows on hover, beside a column, under a row.
        return (
          <Tooltip
            key={option.value}
            variant="label"
            content={option.label}
            placement={vertical ? 'right' : 'default'}
            fullWidth={fullWidth || vertical}
            className={fullWidth && !vertical ? 'min-w-0 flex-1' : undefined}
          >
            {segment}
          </Tooltip>
        )
      })}
    </div>
  )
}
